// @ts-check

/**
 * @file Renders the accounts index route.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native"
import {Account} from "../../src/frontend-models/account"
import Button from "../../src/components/button"
import buildRoutePath from "../../src/shared/build-route-path"
import EmptyState from "../../src/components/empty-state"
import errorMessage from "../../src/shared/error-message"
import Link from "../../src/components/link"
import LoadingState from "../../src/components/loading-state"
import Pagination from "../../src/components/pagination"
import ScreenTitle from "../../src/components/screen-title"
import TextInput from "../../src/components/text-input"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {"all" | "member" | "owner"} AccountsRoleFilter */
/** @typedef {(key: string, interpolation?: Record<string, unknown>) => string} AccountTranslator */

/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30

/** @typedef {{[key: string]: unknown}} AccountsIndexScreenProps */

/**
 * @typedef {object} AccountsIndexScreenState
 * @property {Account[]} accounts Loaded account rows.
 * @property {boolean} loading Whether account rows are loading.
 * @property {number} page Current result page.
 * @property {AccountsRoleFilter} roleFilter Active role filter.
 * @property {string} searchQuery Search text filter.
 * @property {{kind: "error", text: string} | null} status Error status shown above results.
 * @property {number} totalCount Number of matching accounts.
 */

/**
 * Renders filters, responsive account lists, and pagination.
 * @augments {ShapeComponent<AccountsIndexScreenProps, AccountsIndexScreenState>}
 */
class AccountsIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  loadRequestId = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {AccountsIndexScreenState} */
  state = {
    accounts: /** @type {Account[]} */ [],
    loading: true,
    page: 1,
    roleFilter: /** @type {AccountsRoleFilter} */ ("all"),
    searchQuery: "",
    status: /** @type {{kind: "error", text: string} | null} */ null,
    totalCount: 0
  }

  /**
   * Initializes translations, responsive breakpoints, and data loading.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    const breakpoint = useBreakpoint()
    const params = useLocalSearchParams()

    this.breakpoint = breakpoint
    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const page = this.s.page
    const roleFilter = this.s.roleFilter
    const searchQuery = this.s.searchQuery
    useEffect(() => {
      void this.loadAccounts()
    }, [page, roleFilter, searchQuery])
    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the accounts index route.
   * @returns {ReactNode} Accounts index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="accountsIndexScreen"
      >
        <ScreenTitle>{_("Accounts")}</ScreenTitle>
        <View
          testID="accountsIndexScreen/content"
        >
          <View
            style={styles.header ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 24
            }}
            testID="accountsIndexScreen/header"
          >
            <View testID="accountsIndexScreen/header/text">
              <Text
                style={styles[`headerSubtitle-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontWeight: 600,
                  marginBottom: 6
                }}
                testID="accountsIndexScreen/header/subtitle"
              >
                {_("Browse and manage accounts")}
              </Text>
              <Text
                style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                  fontSize: 28,
                  fontWeight: 700
                }}
                testID="accountsIndexScreen/header/title"
              >
                {_("Accounts")}
              </Text>
            </View>
            <Button
              href={buildRoutePath(this.tt.params, "/accounts/new")}
              iconName="plus"
              label={_("New account")}
              testID="accountsIndexScreen/newButton"
            />
          </View>
          {this.renderSearchAndFilters()}
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="accountsIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading && <LoadingState testID="accountsIndexScreen/loadingText" />}
          {!loading && this.renderAccountsList()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders pagination when rows are loaded.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="accountsIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Renders account search and role filters.
   * @returns {ReactNode} Search and filter controls.
   */
  renderSearchAndFilters() {
    const {roleFilter} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.searchAndFilters ||= {
          marginBottom: 16
        }}
        testID="accountsIndexScreen/searchAndFilters"
      >
        <TextInput
          onChangeText={this.tt.onSearchChangeText}
          placeholder={_("Search accounts...")}
          style={styles.searchInput ||= {borderRadius: 10, marginBottom: 12}}
          testID="accountsIndexScreen/searchInput"
        />
        <View
          style={styles.filterRow ||= {
            flexDirection: "row",
            gap: 8
          }}
          testID="accountsIndexScreen/filterRow"
        >
          <Pressable
            onPress={this.tt.onAllFilterPress}
            style={styles[`filterButton-${themeName}-${roleFilter === "all"}`] ||= {
              alignItems: "center",
              backgroundColor: roleFilter === "all" ? colors.infoBackground : colors.surface,
              borderColor: roleFilter === "all" ? colors.info : colors.borderStrong,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 12,
              paddingVertical: 8
            }}
            testID="accountsIndexScreen/filterAll"
          >
            <View
              style={styles.filterIcon ||= {
                marginRight: 8,
                width: 14
              }}
              testID="accountsIndexScreen/filterAll/icon"
            >
              <FontAwesome6 color={roleFilter === "all" ? colors.link : colors.textMuted} name="layer-group" size={13} />
            </View>
            <Text
              style={styles[`filterButtonText-${themeName}-${roleFilter === "all"}`] ||= {
                color: roleFilter === "all" ? colors.link : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="accountsIndexScreen/filterAll/text"
            >
              {_("All")}
            </Text>
          </Pressable>
          <Pressable
            onPress={this.tt.onMemberFilterPress}
            style={styles[`filterButton-${themeName}-${roleFilter === "member"}`] ||= {
              alignItems: "center",
              backgroundColor: roleFilter === "member" ? colors.infoBackground : colors.surface,
              borderColor: roleFilter === "member" ? colors.info : colors.borderStrong,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 12,
              paddingVertical: 8
            }}
            testID="accountsIndexScreen/filterMember"
          >
            <View
              style={styles.filterIcon}
              testID="accountsIndexScreen/filterMember/icon"
            >
              <FontAwesome6 color={roleFilter === "member" ? colors.link : colors.textMuted} name="user" size={13} />
            </View>
            <Text
              style={styles[`filterButtonText-${themeName}-${roleFilter === "member"}`] ||= {
                color: roleFilter === "member" ? colors.link : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="accountsIndexScreen/filterMember/text"
            >
              {_("Member")}
            </Text>
          </Pressable>
          <Pressable
            onPress={this.tt.onOwnerFilterPress}
            style={styles[`filterButton-${themeName}-${roleFilter === "owner"}`] ||= {
              alignItems: "center",
              backgroundColor: roleFilter === "owner" ? colors.infoBackground : colors.surface,
              borderColor: roleFilter === "owner" ? colors.info : colors.borderStrong,
              borderRadius: 999,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 12,
              paddingVertical: 8
            }}
            testID="accountsIndexScreen/filterOwner"
          >
            <View
              style={styles.filterIcon}
              testID="accountsIndexScreen/filterOwner/icon"
            >
              <FontAwesome6 color={roleFilter === "owner" ? colors.link : colors.textMuted} name="user-shield" size={13} />
            </View>
            <Text
              style={styles[`filterButtonText-${themeName}-${roleFilter === "owner"}`] ||= {
                color: roleFilter === "owner" ? colors.link : colors.textMuted,
                fontSize: 13,
                fontWeight: 700
              }}
              testID="accountsIndexScreen/filterOwner/text"
            >
              {_("Owner")}
            </Text>
          </Pressable>
        </View>
      </View>
    )
  }

  /**
   * Renders the responsive account list.
   * @returns {ReactNode} Account list content or empty state.
   */
  renderAccountsList() {
    const {accounts} = this.s
    if (accounts.length === 0) {
      return (
        <EmptyState
          message={_("No accounts found")}
          testID="accountsIndexScreen/emptyCard"
        />
      )
    }
    if (this.tt.breakpoint.xlUp) return this.renderTable()
    if (this.tt.breakpoint.mdUp) return this.renderCards()
    return this.renderCompactList()
  }

  /**
   * Renders the desktop account table.
   * @returns {ReactNode} Account table.
   */
  renderTable() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles[`tableWrapper-${themeName}`] ||= {
          backgroundColor: colors.surface,
          borderColor: colors.borderStrong,
          borderRadius: 12,
          borderWidth: 1,
          overflow: "hidden"
        }}
        testID="accountsIndexScreen/table"
      >
        <View
          style={styles[`tableHeader-${themeName}`] ||= {
            backgroundColor: colors.surfaceMuted,
            borderBottomColor: colors.divider,
            borderBottomWidth: 1,
            flexDirection: "row",
            paddingHorizontal: 16,
            paddingVertical: 12
          }}
          testID="accountsIndexScreen/table/header"
        >
          <Text
            style={styles[`tableHeaderName-${themeName}`] ||= {
              color: colors.textMuted,
              flex: 1,
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase"
            }}
            testID="accountsIndexScreen/table/header/name"
          >
            {_("Account")}
          </Text>
          <Text
            style={styles[`tableHeaderRole-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 700,
              textAlign: "center",
              textTransform: "uppercase",
              width: 120
            }}
            testID="accountsIndexScreen/table/header/role"
          >
            {_("Role")}
          </Text>
          <Text
            style={styles[`tableHeaderActivity-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 700,
              textAlign: "right",
              textTransform: "uppercase",
              width: 220
            }}
            testID="accountsIndexScreen/table/header/activity"
          >
            {_("Last activity")}
          </Text>
        </View>
        {this.s.accounts.map((account) => this.renderTableRow(account))}
      </View>
    )
  }

  /**
   * Renders one table row.
   * @param {Account} account Account to render.
   * @returns {ReactNode} Account table row.
   */
  renderTableRow(account) {
    const accountId = String(account.id())
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        key={`table-${accountId}`}
        style={styles[`tableRow-${themeName}`] ||= {
          borderBottomColor: colors.divider,
          borderBottomWidth: 1,
          flexDirection: "row",
          paddingHorizontal: 16,
          paddingVertical: 14
        }}
        testID={`accountsIndexScreen/account-${accountId}`}
      >
        <Link
          href={buildRoutePath(this.tt.params, `/accounts/${accountId}`)}
          style={styles.tableNameLink ||= {
            flex: 1
          }}
          testID={`accountsIndexScreen/account-${accountId}/showLink`}
        >
          <Text
            style={styles[`tableNameText-${themeName}`] ||= {
              fontSize: 15,
              fontWeight: 700
            }}
            testID={`accountsIndexScreen/account-${accountId}/name`}
          >
            {account.name()}
          </Text>
        </Link>
        <View
          style={styles.tableRoleCell ||= {
            alignItems: "center",
            justifyContent: "center",
            width: 120
          }}
          testID={`accountsIndexScreen/account-${accountId}/tableRole`}
        >
          {this.renderRoleBadge(accountId)}
        </View>
        <Text
          style={styles[`tableActivityText-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 13,
            textAlign: "right",
            width: 220
          }}
          testID={`accountsIndexScreen/account-${accountId}/activity`}
        >
          {lastActivityText(_, account)}
        </Text>
      </View>
    )
  }

  /**
   * Renders medium-width account cards.
   * @returns {ReactNode} Account card grid.
   */
  renderCards() {
    return (
      <View
        style={styles.cardGrid ||= {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 12
        }}
        testID="accountsIndexScreen/cards"
      >
        {this.s.accounts.map((account) => this.renderCard(account))}
      </View>
    )
  }

  /**
   * Renders one account card.
   * @param {Account} account Account to render.
   * @returns {ReactNode} Account card.
   */
  renderCard(account) {
    const accountId = String(account.id())
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        key={`card-${accountId}`}
        style={styles[`accountCard-${this.tt.breakpoint.name}`] ||= this.tt.breakpoint.styling({base: {width: "100%"}, mdUp: {width: "48%"}, lgUp: {width: "49%"}})}
        testID={`accountsIndexScreen/account-${accountId}`}
      >
        <View
          style={styles[`accountCard-${themeName}`] ||= {
            backgroundColor: colors.surface,
            borderColor: colors.border,
            borderRadius: 12,
            borderWidth: 1,
            padding: 16
          }}
          testID={`accountsIndexScreen/account-${accountId}/card`}
        >
          <View
            style={styles.cardHeader ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 12
            }}
            testID={`accountsIndexScreen/account-${accountId}/cardHeader`}
          >
            <View
              style={styles[`cardAvatar-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.infoBackground,
                borderRadius: 12,
                height: 44,
                justifyContent: "center",
                width: 44
              }}
              testID={`accountsIndexScreen/account-${accountId}/avatar`}
            >
              <Text
                style={styles[`cardAvatarText-${themeName}`] ||= {
                  color: colors.link,
                  fontSize: 15,
                  fontWeight: 700
                }}
                testID={`accountsIndexScreen/account-${accountId}/avatarText`}
              >
                {accountInitials(account)}
              </Text>
            </View>
            {this.renderRoleBadge(accountId)}
          </View>
          <Link
            href={buildRoutePath(this.tt.params, `/accounts/${accountId}`)}
            testID={`accountsIndexScreen/account-${accountId}/showLink`}
          >
            <Text
              style={styles.cardName ||= {
                fontSize: 17,
                fontWeight: 700
              }}
              testID={`accountsIndexScreen/account-${accountId}/name`}
            >
              {account.name()}
            </Text>
          </Link>
          <Text
            style={styles[`cardActivity-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 13,
              marginTop: 6
            }}
            testID={`accountsIndexScreen/account-${accountId}/cardActivity`}
          >
            {lastActivityText(_, account)}
          </Text>
        </View>
      </View>
    )
  }

  /**
   * Renders compact account rows.
   * @returns {ReactNode} Compact account list.
   */
  renderCompactList() {
    return (
      <View
        style={styles.compactList ||= {
          gap: 10
        }}
        testID="accountsIndexScreen/compactList"
      >
        {this.s.accounts.map((account) => this.renderCompactListItem(account))}
      </View>
    )
  }

  /**
   * Renders one compact account row.
   * @param {Account} account Account to render.
   * @returns {ReactNode} Compact account row.
   */
  renderCompactListItem(account) {
    const accountId = String(account.id())
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Link
        key={`list-${accountId}`}
        href={buildRoutePath(this.tt.params, `/accounts/${accountId}`)}
        testID={`accountsIndexScreen/account-${accountId}/showLink`}
      >
        <View
          style={styles[`compactRow-${themeName}`] ||= {
            alignItems: "center",
            backgroundColor: colors.surface,
            borderColor: colors.border,
            borderRadius: 12,
            borderWidth: 1,
            flexDirection: "row",
            gap: 12,
            padding: 14
          }}
          testID={`accountsIndexScreen/account-${accountId}`}
        >
          <View
            style={styles[`compactAvatar-${themeName}`] ||= {
              alignItems: "center",
              backgroundColor: colors.infoBackground,
              borderRadius: 10,
              height: 40,
              justifyContent: "center",
              width: 40
            }}
            testID={`accountsIndexScreen/account-${accountId}/compactAvatar`}
          >
            <Text
              style={styles[`compactAvatarText-${themeName}`] ||= {
                color: colors.link,
                fontSize: 14,
                fontWeight: 700
              }}
              testID={`accountsIndexScreen/account-${accountId}/compactAvatarText`}
            >
              {accountInitials(account)}
            </Text>
          </View>
          <View
            style={styles.compactContent ||= {
              flex: 1
            }}
            testID={`accountsIndexScreen/account-${accountId}/compactContent`}
          >
            <Text
              style={styles.compactName ||= {
                fontSize: 15,
                fontWeight: 700
              }}
              testID={`accountsIndexScreen/account-${accountId}/name`}
            >
              {account.name()}
            </Text>
            <Text
              style={styles[`compactActivity-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 12,
                marginTop: 4
              }}
              testID={`accountsIndexScreen/account-${accountId}/compactActivity`}
            >
              {lastActivityText(_, account)}
            </Text>
          </View>
          {this.renderRoleBadge(accountId)}
        </View>
      </Link>
    )
  }

  /**
   * Renders an account role badge.
   * @param {string} accountId Account id used in test ids.
   * @returns {ReactNode} Small role label element.
   */
  renderRoleBadge(accountId) {
    return (
      <View
        style={styles.roleBadge ||= {
          backgroundColor: "#e5e7eb",
          borderRadius: 999,
          paddingHorizontal: 10,
          paddingVertical: 4
        }}
        testID={`accountsIndexScreen/account-${accountId}/roleBadge`}
      >
        <Text
          style={styles.roleBadgeText ||= {
            color: "#374151",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase"
          }}
          testID={`accountsIndexScreen/account-${accountId}/roleText`}
        >
          {_("Member")}
        </Text>
      </View>
    )
  }

  /**
   * Debounces account search updates.
   * @param {string} value Search text entered by the user.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      if (value !== this.s.searchQuery) {
        this.s.page = 1
        this.s.searchQuery = value
      }
    }, 300)
  }

  /**
   * Updates the active result page.
   * @param {number} nextPage Page selected by pagination.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Applies the all-accounts filter.
   * @returns {void} Nothing.
   */
  onAllFilterPress = () => {
    this.applyRoleFilter("all")
  }

  /**
   * Applies the member role filter.
   * @returns {void} Nothing.
   */
  onMemberFilterPress = () => {
    this.applyRoleFilter("member")
  }

  /**
   * Applies the owner role filter.
   * @returns {void} Nothing.
   */
  onOwnerFilterPress = () => {
    this.applyRoleFilter("owner")
  }

  /**
   * Applies an account role filter.
   * @param {AccountsRoleFilter} roleFilter Role filter to apply.
   * @returns {void} Nothing.
   */
  applyRoleFilter(roleFilter) {
    if (roleFilter === this.s.roleFilter) return
    this.s.page = 1
    this.s.roleFilter = roleFilter
  }

  /**
   * Loads accounts for the active filters.
   * @returns {Promise<void>} Resolves after account rows are loaded.
   * @rejects {Error} When account loading fails.
   */
  loadAccounts = async () => {
    const {page, roleFilter, searchQuery} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      if (roleFilter === "owner") {
        this.s.accounts = []
        this.s.loading = false
        this.s.totalCount = 0
        return
      }
      /** @type {Record<string, unknown>} */
      const ransackParams = {
        s: "name asc, id asc"
      }
      if (searchQuery) ransackParams.nameCont = searchQuery
      const baseQuery = Account.ransack(ransackParams)
      const [loadedAccounts, totalCount] = await Promise.all([
        baseQuery.clone().page(page).perPage(PAGE_SIZE).toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.accounts = filterAccountsByRole(loadedAccounts, roleFilter)
      this.s.loading = false
      this.s.totalCount = totalCount
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<AccountsIndexScreenProps>} */
const AccountsIndexScreenComponent = memo(shapeComponent(AccountsIndexScreen))

export default AccountsIndexScreenComponent

/**
 * Filters account rows by role.
 * @param {Account[]} accounts Account rows to filter.
 * @param {AccountsRoleFilter} roleFilter Role filter to apply.
 * @returns {Account[]} Filtered account rows.
 */
function filterAccountsByRole(accounts, roleFilter) {
  if (roleFilter === "all") return accounts
  if (roleFilter === "member") return accounts
  return []
}

/**
 * Builds initials for an account avatar.
 * @param {Account} account Account to label.
 * @returns {string} Avatar initials.
 */
function accountInitials(account) {
  const name = account.name()
  if (!name) return "?"
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
}

/**
 * Builds last activity text for an account row.
 * @param {AccountTranslator} _ Translation function.
 * @param {Account} account Account to describe.
 * @returns {string} Last activity label.
 */
function lastActivityText(_, account) {
  const updatedAt = account.updatedAt()
  if (!updatedAt) return _("Last activity unknown")
  return _("Updated at %{date}", {
    date: new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(updatedAt)
  })
}
